import { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { themeGet } from '@styled-system/theme-get';
import { Flex, Button, Text, TextField, Tabs } from '@radix-ui/themes';

import RadixDialog from '../../../shared/RadixDialog';
import { useNotification } from '../../../shared/Notification/useNotification';
import { useBroadcastContext } from '../contexts/BroadcastContext.jsx';
import {
  slugifyKey,
  missingCount,
  computeDuplicateOf,
  parseCsvRows,
  buildCsvTemplate,
} from '../utils/recipientFields';
import {
  useCreateRecipientFieldMutation,
  useUpdateRecipientFieldMutation,
  useDestroyRecipientFieldMutation,
  useMatchRecipientRowsMutation,
} from '../hooks/useRecipientFieldMutations';

// Above this many recipients the dialog opens on the Import CSV tab.
const LARGE_AUDIENCE_ROWS = 200;

// Create/edit modal for a broadcast recipient field. The audience is shown
// as a table keyed by employee ID (`field.values[userUuid]` prefills each
// row); an admin fills a value per person by typing, pasting a column, or
// importing a CSV matched on employee ID, email, or phone number.
// `onCreated(field)` fires after a brand-new field is saved, so the composer
// that opened the dialog from "Insert Field" can drop the token in place.
export const RecipientFieldDialog = ({
  isOpen,
  onClose,
  field = null,
  onCreated = null,
}) => {
  const { t } = useTranslation('broadcasts');
  const { showDefault } = useNotification();
  const { values, recipientRows = [] } = useBroadcastContext();
  const {
    createRecipientFieldMutation,
    isLoading: isCreating,
  } = useCreateRecipientFieldMutation();
  const {
    updateRecipientFieldMutation,
    isLoading: isUpdating,
  } = useUpdateRecipientFieldMutation();
  const {
    destroyRecipientFieldMutation,
    isLoading: isDeleting,
  } = useDestroyRecipientFieldMutation();
  const {
    matchRecipientRowsMutation,
    isLoading: isMatching,
  } = useMatchRecipientRowsMutation();

  const isEditing = !!field;
  const isSaving = isCreating || isUpdating;
  const fileInputRef = useRef(null);

  // Hands the admin a CSV pre-filled with this audience's identifiers so the
  // only thing left to do is fill the value column and import it back.
  const handleDownloadTemplate = () => {
    const blob = new Blob([buildCsvTemplate(recipientRows, name || 'Value')], {
      type: 'text/csv;charset=utf-8',
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');

    link.href = url;
    link.download = `${key || 'recipient-field'}-template.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const [name, setName] = useState(field?.name || '');
  const [key, setKey] = useState(field?.key || '');
  const [keyEditedManually, setKeyEditedManually] = useState(!!field);
  // A large audience is filled in via CSV import, so open on that tab and
  // only render the per-recipient table (one input per row) when asked.
  const [activeTab, setActiveTab] = useState(() =>
    recipientRows.length > LARGE_AUDIENCE_ROWS ? 'csv' : 'table',
  );
  const [valuesByUuid, setValuesByUuid] = useState(() => ({
    ...(field?.values || {}),
  }));
  const [matchResult, setMatchResult] = useState(null);
  const [csvFileName, setCsvFileName] = useState('');

  const rowOrder = useMemo(() => recipientRows.map(row => row.userUuid), [
    recipientRows,
  ]);
  const totalCount = recipientRows.length;
  const filledCount = rowOrder.filter(uuid => (valuesByUuid[uuid] || '').trim())
    .length;
  const missing = missingCount(filledCount, totalCount);
  const coveragePct =
    totalCount > 0 ? Math.round((filledCount / totalCount) * 100) : 0;

  const duplicateOf = useMemo(
    () =>
      computeDuplicateOf(
        recipientRows.map(row => ({
          userUuid: row.userUuid,
          value: valuesByUuid[row.userUuid],
        })),
      ),
    [recipientRows, valuesByUuid],
  );
  const duplicateCount = Object.keys(duplicateOf).length;

  const nameByUuid = useMemo(
    () =>
      Object.fromEntries(recipientRows.map(row => [row.userUuid, row.name])),
    [recipientRows],
  );

  const handleNameChange = value => {
    setName(value);
    if (!keyEditedManually) {
      setKey(slugifyKey(value));
    }
  };

  const handleKeyChange = value => {
    setKeyEditedManually(true);
    setKey(value);
  };

  const handleValueChange = (userUuid, value) => {
    setValuesByUuid(prev => ({ ...prev, [userUuid]: value }));
  };

  // Pasting a multi-line column (e.g. from Sheets) fills the focused row and
  // every row after it, in table order.
  const handlePaste = (userUuid, e) => {
    const text = e.clipboardData?.getData('text');
    if (!text) return;

    const lines = text.split(/\r?\n/).filter(line => line.trim() !== '');
    if (lines.length <= 1) return;

    e.preventDefault();
    const startIndex = rowOrder.indexOf(userUuid);

    setValuesByUuid(prev => {
      const next = { ...prev };
      lines.forEach((line, i) => {
        const uuid = rowOrder[startIndex + i];
        if (uuid) next[uuid] = line.trim();
      });
      return next;
    });
  };

  const canSave = name.trim().length > 0 && key.trim().length > 0;

  const buildRows = () =>
    recipientRows.map(row => ({
      userUuid: row.userUuid,
      value: (valuesByUuid[row.userUuid] || '').trim(),
    }));

  const handleSaveSuccess = ({ data }) => {
    if (data?.rejected?.length > 0) {
      // Rows for users no longer in this broadcast's audience (e.g. the
      // audience changed after the field was created) are dropped rather
      // than saved; surface that rather than silently losing them.
      showDefault({
        content: t('recipientFieldDialog.rejectedRows', {
          count: data.rejected.length,
        }),
      });
    }
    onClose();
    // After the dialog has released focus, so the composer can insert the
    // new field's token at the editor's cursor.
    if (!isEditing && onCreated && data?.recipientField) {
      onCreated(data.recipientField);
    }
  };

  const handleSave = () => {
    const payload = {
      broadcastMessageUuid: values.uuid,
      name,
      key,
      skipMissing: field ? field.skipMissing : true,
      rows: buildRows(),
    };

    if (isEditing) {
      updateRecipientFieldMutation(
        { ...payload, id: field.id },
        { onSuccess: handleSaveSuccess },
      );
    } else {
      createRecipientFieldMutation(payload, { onSuccess: handleSaveSuccess });
    }
  };

  const handleDelete = () => {
    destroyRecipientFieldMutation(
      { broadcastMessageUuid: values.uuid, id: field.id },
      { onSuccess: onClose },
    );
  };

  const runMatch = rows => {
    matchRecipientRowsMutation(
      { broadcastMessageUuid: values.uuid, rows },
      { onSuccess: ({ data }) => setMatchResult(data) },
    );
  };

  const handleCsvText = (text, fileName) => {
    setCsvFileName(fileName || '');
    runMatch(parseCsvRows(text, name));
  };

  const handleFileInput = async e => {
    const file = e.target.files?.[0];
    if (!file) return;
    handleCsvText(await file.text(), file.name);
  };

  const handleDrop = async e => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (!file) return;
    handleCsvText(await file.text(), file.name);
  };

  const handleApplyToTable = () => {
    if (!matchResult) return;

    setValuesByUuid(prev => {
      const next = { ...prev };
      matchResult.matched.forEach(row => {
        next[row.userUuid] = row.value;
      });
      return next;
    });
    setActiveTab('table');
  };

  const handleUseSuggestion = row => {
    if (!row.suggestion) return;
    handleValueChange(row.suggestion.userUuid, row.value);
    setMatchResult(prev => ({
      ...prev,
      matched: [
        ...prev.matched,
        {
          userUuid: row.suggestion.userUuid,
          name: row.suggestion.name,
          value: row.value,
        },
      ],
      unmatched: prev.unmatched.filter(r => r !== row),
    }));
  };

  return (
    <RadixDialog
      isOpen={isOpen}
      onClose={onClose}
      title={
        isEditing
          ? t('recipientFieldDialog.editTitle', { name: field.name })
          : t('recipientFieldDialog.newTitle')
      }
    >
      <Wrapper>
        <Flex direction="column" gap="3">
          <Flex gap="3">
            <Flex direction="column" gap="1" flexGrow="1">
              <Text as="label" size="2" weight="medium">
                {t('recipientFieldDialog.nameLabel')}
              </Text>
              <TextField.Root
                value={name}
                placeholder={t('recipientFieldDialog.namePlaceholder')}
                onChange={e => handleNameChange(e.target.value)}
              />
            </Flex>
            <Flex direction="column" gap="1" flexGrow="1">
              <Text as="label" size="2" weight="medium">
                {t('recipientFieldDialog.keyLabel')}
              </Text>
              <TextField.Root
                value={key}
                placeholder={t('recipientFieldDialog.keyPlaceholder')}
                onChange={e => handleKeyChange(e.target.value)}
              />
            </Flex>
          </Flex>

          <CoverageRow>
            <Text
              size="2"
              weight="medium"
              color={missing > 0 ? 'orange' : 'green'}
            >
              {t('recipientFieldDialog.coverage', {
                filledCount,
                totalCount,
              })}
            </Text>
            <CoverageBar>
              <CoverageBarFill $pct={coveragePct} $warn={missing > 0} />
            </CoverageBar>
          </CoverageRow>

          <Tabs.Root value={activeTab} onValueChange={setActiveTab}>
            <Tabs.List>
              <Tabs.Trigger value="table">
                {t('recipientFieldDialog.tabTable')}
              </Tabs.Trigger>
              <Tabs.Trigger value="csv">
                {t('recipientFieldDialog.tabCsv')}
              </Tabs.Trigger>
            </Tabs.List>

            <Tabs.Content value="table">
              <TableWrap>
                <Table>
                  <thead>
                    <tr>
                      <th>{t('recipientFieldDialog.recipientColumn')}</th>
                      <th>{t('recipientFieldDialog.employeeIdColumn')}</th>
                      <th>
                        {t('recipientFieldDialog.valueColumn', {
                          name: name || t('recipientFieldDialog.newTitle'),
                        })}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {recipientRows.map(row => {
                      const value = valuesByUuid[row.userUuid] || '';
                      const isMissingRow = value.trim() === '';
                      const isDuplicate = !!duplicateOf[row.userUuid];

                      return (
                        <TableRow
                          key={row.userUuid}
                          $missing={isMissingRow}
                          $duplicate={isDuplicate}
                        >
                          <td>
                            <div>{row.name}</div>
                            <EmailText>{row.email}</EmailText>
                          </td>
                          <td className="mono">{row.employeeId}</td>
                          <td>
                            <ValueInput
                              className="mono"
                              value={value}
                              placeholder={t(
                                'recipientFieldDialog.valuePlaceholder',
                              )}
                              onChange={e =>
                                handleValueChange(row.userUuid, e.target.value)
                              }
                              onPaste={e => handlePaste(row.userUuid, e)}
                            />
                            {isDuplicate && (
                              <DuplicateFlag>
                                {t('recipientFieldDialog.duplicateOf', {
                                  name: nameByUuid[duplicateOf[row.userUuid]],
                                })}
                              </DuplicateFlag>
                            )}
                          </td>
                        </TableRow>
                      );
                    })}
                  </tbody>
                </Table>
              </TableWrap>
              <DuplicateFooter>
                {duplicateCount > 0
                  ? t('recipientFieldDialog.duplicateCount', {
                      count: duplicateCount,
                    })
                  : t('recipientFieldDialog.noDuplicates')}
              </DuplicateFooter>
            </Tabs.Content>

            <Tabs.Content value="csv">
              <CsvTab>
                <DropZone
                  onDragOver={e => e.preventDefault()}
                  onDrop={handleDrop}
                  onClick={() => fileInputRef.current?.click()}
                >
                  <Text size="2" color="gray">
                    {t('recipientFieldDialog.dropZone')}
                  </Text>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv,text/csv"
                    hidden
                    onChange={handleFileInput}
                  />
                </DropZone>

                <TemplateHint>
                  <Text size="1" color="gray">
                    {t('recipientFieldDialog.templateHint')}
                  </Text>
                  <TemplateLink type="button" onClick={handleDownloadTemplate}>
                    {t('recipientFieldDialog.downloadTemplate')}
                  </TemplateLink>
                </TemplateHint>

                {isMatching && (
                  <Text size="2" color="gray">
                    {t('recipientFieldDialog.matching')}
                  </Text>
                )}

                {matchResult && (
                  <>
                    <CsvPreview>
                      <CsvPreviewHeader>
                        <span>{csvFileName}</span>
                        <span>{t('recipientFieldDialog.matchColumn')}</span>
                      </CsvPreviewHeader>
                      {matchResult.matched.map((row, i) => (
                        <CsvPreviewRow key={`matched-${row.userUuid}-${i}`}>
                          <span>
                            {row.employeeId || row.email || row.phone}
                          </span>
                          <MatchOk>{row.name}</MatchOk>
                        </CsvPreviewRow>
                      ))}
                      {matchResult.unmatched.map((row, i) => (
                        <CsvPreviewRow key={`unmatched-${i}`}>
                          <span>
                            {row.employeeId || row.email || row.phone}
                          </span>
                          <MatchWarn>
                            {row.suggestion
                              ? t('recipientFieldDialog.suggestion', {
                                  employeeId: row.suggestion.employeeId,
                                  name: row.suggestion.name,
                                })
                              : t('recipientFieldDialog.noMatch')}
                            {row.suggestion && (
                              <UseSuggestionButton
                                type="button"
                                onClick={() => handleUseSuggestion(row)}
                              >
                                {t('recipientFieldDialog.useSuggestion')}
                              </UseSuggestionButton>
                            )}
                          </MatchWarn>
                        </CsvPreviewRow>
                      ))}
                    </CsvPreview>
                    <Flex justify="end">
                      <Button
                        variant="soft"
                        onClick={handleApplyToTable}
                        disabled={matchResult.matched.length === 0}
                      >
                        {t('recipientFieldDialog.applyToTable')}
                      </Button>
                    </Flex>
                  </>
                )}
              </CsvTab>
            </Tabs.Content>
          </Tabs.Root>

          <Flex gap="3" justify="between" mt="2">
            <div>
              {isEditing && (
                <Button
                  variant="outline"
                  color="red"
                  onClick={handleDelete}
                  disabled={isDeleting}
                >
                  {t('recipientFieldDialog.delete')}
                </Button>
              )}
            </div>
            <Flex gap="3">
              <Button variant="outline" color="gray" onClick={onClose}>
                {t('recipientFieldDialog.cancel')}
              </Button>
              <Button onClick={handleSave} disabled={!canSave || isSaving}>
                {t('recipientFieldDialog.save')}
              </Button>
            </Flex>
          </Flex>
        </Flex>
      </Wrapper>
    </RadixDialog>
  );
};

const Wrapper = styled.div`
  width: 820px;
  max-width: 80vw;
`;

const CoverageRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const CoverageBar = styled.div`
  flex: 1;
  height: 6px;
  border-radius: 3px;
  background: ${themeGet('colors.black.10')};
  overflow: hidden;
`;

const CoverageBarFill = styled.div`
  height: 100%;
  width: ${props => props.$pct}%;
  background: ${props =>
    props.$warn ? themeGet('colors.orange.100') : themeGet('colors.green.100')};
  transition: width 0.2s;
`;

const TableWrap = styled.div`
  max-height: 320px;
  overflow-y: auto;
  overflow-x: auto;
  border: 1px solid ${themeGet('colors.black.10')};
  border-radius: 8px;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 12.5px;

  th {
    position: sticky;
    top: 0;
    background: ${themeGet('colors.black.5')};
    text-align: left;
    font-size: 10.5px;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: ${themeGet('colors.black.60')};
    padding: 9px 16px;
  }

  td {
    padding: 8px 16px;
    vertical-align: middle;
    border-top: 1px solid ${themeGet('colors.black.10')};

    &.mono {
      font-family: monospace;
    }
  }
`;

const TableRow = styled.tr`
  background: ${props => {
    if (props.$missing || props.$duplicate) return '#fff8e6';
    return 'transparent';
  }};
`;

const EmailText = styled.div`
  color: ${themeGet('colors.black.60')};
  font-size: 11px;
`;

const ValueInput = styled.input`
  width: 100%;
  font: inherit;
  font-size: 12.5px;
  padding: 6px 10px;
  border: 1px solid ${themeGet('colors.black.20')};
  border-radius: 6px;

  &:focus {
    outline: none;
    border-color: ${themeGet('colors.primary.100')};
  }

  &.mono {
    font-family: monospace;
  }
`;

const DuplicateFlag = styled.div`
  font-size: 10.5px;
  color: #b45309;
  font-weight: 600;
  margin-top: 3px;
`;

const DuplicateFooter = styled.div`
  font-size: 11.5px;
  color: ${themeGet('colors.black.60')};
  padding-top: 4px;
`;

const CsvTab = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const DropZone = styled.div`
  border: 1.5px dashed ${themeGet('colors.black.20')};
  border-radius: 8px;
  padding: 16px;
  text-align: center;
  cursor: pointer;
`;

const TemplateHint = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
`;

const TemplateLink = styled.button`
  background: none;
  border: 0;
  padding: 0;
  cursor: pointer;
  font: inherit;
  font-size: 12px;
  font-weight: 600;
  color: ${themeGet('colors.primary.100')};

  &:hover {
    text-decoration: underline;
  }
`;

const CsvPreview = styled.div`
  border: 1px solid ${themeGet('colors.black.10')};
  border-radius: 8px;
  overflow: hidden;
  max-height: 240px;
  overflow-y: auto;
`;

const CsvPreviewHeader = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  padding: 8px 14px;
  font-size: 10.5px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: ${themeGet('colors.black.60')};
  background: ${themeGet('colors.black.5')};
`;

const CsvPreviewRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  padding: 8px 14px;
  font-size: 12px;
  border-top: 1px solid ${themeGet('colors.black.10')};
`;

const MatchOk = styled.span`
  font-weight: 600;
  color: ${themeGet('colors.green.100')};
`;

const MatchWarn = styled.span`
  font-weight: 600;
  color: #b45309;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const UseSuggestionButton = styled.button`
  all: unset;
  cursor: pointer;
  color: ${themeGet('colors.primary.100')};
  font-weight: 600;
  font-size: 11px;
`;
